import { useState, useMemo, useRef, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Check, ArrowLeft, Mail, Loader2, Phone, CheckCircle, Heart, Users, ClipboardList } from "lucide-react";
import Navbar from "@/components/Navbar";
import { useLanguage } from "@/i18n/LanguageContext";
import { useCMS } from "@/lib/CMSContext";
import { trackPlanSelected, trackContactClicked } from "@/hooks/usePageTracking";
import { invokePublicEdgeFunction } from "@/lib/admin-auth";
import {
  CARE_NEED_KEYS,
  PARENT_AGE_KEYS,
  LIVING_SITUATION_KEYS,
  TIMELINE_KEYS,
  CONTACT_METHOD_KEYS,
  SERVICE_COUNTRY_KEYS,
  type CareNeedKey,
  type ParentAgeKey,
  type LivingSituationKey,
  type TimelineKey,
  type ContactMethodKey,
  type ServiceCountryKey,
  type InquiryDetails,
} from "@/lib/order-inquiry";

const CONTACT_EMAIL = "office@vita-dom.org";

/** text-base on mobile prevents iOS zoom on focus (requires ≥16px) */
const inputClass =
  "w-full min-h-11 rounded-lg border border-border bg-background px-4 py-3 text-base sm:text-sm text-foreground focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition";
const labelClass = "text-sm font-medium text-foreground mb-1.5 block";
const sectionClass = "bg-card border rounded-xl p-4 sm:p-6 mb-4 sm:mb-6";
const chipClass =
  "min-h-11 px-4 py-2.5 rounded-lg text-sm border transition text-left sm:text-center touch-manipulation";

export default function Order() {
  const { t } = useLanguage();
  const { cms } = useCMS();
  const errorRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get("plan") || "";
  const [selectedPlan, setSelectedPlan] = useState<string | null>(
    ["pricing_essential", "pricing_comfort", "pricing_premium"].includes(planParam)
      ? planParam
      : null
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [parentName, setParentName] = useState("");
  const [parentAge, setParentAge] = useState<ParentAgeKey | "">("");
  const [parentCountry, setParentCountry] = useState<ServiceCountryKey | "">("");
  const [parentCity, setParentCity] = useState("");
  const [livingSituation, setLivingSituation] = useState<LivingSituationKey | "">("");
  const [careNeeds, setCareNeeds] = useState<CareNeedKey[]>([]);
  const [careNeedsOther, setCareNeedsOther] = useState("");
  const [timeline, setTimeline] = useState<TimelineKey>("exploring");
  const [preferredContact, setPreferredContact] = useState<ContactMethodKey>("email");
  const [message, setMessage] = useState("");

  const plan = selectedPlan ? cms.pricing.find((p) => p.nameKey === selectedPlan) : null;

  const hasContact = name.trim().length > 0 && email.trim().length > 0 && phone.trim().length > 0;
  const hasParent = parentCountry !== "";
  const hasNeeds = careNeeds.length > 0 || careNeedsOther.trim().length > 0;

  const currentStep = useMemo(() => {
    if (selectedPlan && hasContact && hasParent && hasNeeds) return 4;
    if (selectedPlan && hasParent && hasNeeds) return 3;
    if (selectedPlan && hasParent) return 2;
    if (selectedPlan) return 1;
    return 1;
  }, [selectedPlan, hasContact, hasParent, hasNeeds]);

  const steps = [
    { num: 1, label: t("order_step_plan") },
    { num: 2, label: t("order_step_parent") },
    { num: 3, label: t("order_step_needs") },
    { num: 4, label: t("order_step_contact") },
  ];

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [error]);

  const toggleCareNeed = (key: CareNeedKey) => {
    setCareNeeds((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const countryLabel = (key: ServiceCountryKey) => {
    const map: Record<ServiceCountryKey, string> = {
      serbia: t("order_country_serbia"),
      romania: t("order_country_romania"),
      north_macedonia: t("order_country_north_macedonia"),
      bulgaria: t("order_country_bulgaria"),
      hungary: t("order_country_hungary"),
      other: t("order_country_other"),
    };
    return map[key];
  };

  const ageLabel = (key: ParentAgeKey) => {
    const map: Record<ParentAgeKey, string> = {
      "60_69": t("order_parent_age_60_69"),
      "70_79": t("order_parent_age_70_79"),
      "80_89": t("order_parent_age_80_89"),
      "90_plus": t("order_parent_age_90_plus"),
    };
    return map[key];
  };

  const livingLabel = (key: LivingSituationKey) => {
    const map: Record<LivingSituationKey, string> = {
      alone: t("order_living_alone"),
      spouse: t("order_living_spouse"),
      family: t("order_living_family"),
      care_home: t("order_living_care_home"),
    };
    return map[key];
  };

  const needLabel = (key: CareNeedKey) => {
    const map: Record<CareNeedKey, string> = {
      companionship: t("order_need_companionship"),
      meals: t("order_need_meals"),
      medication: t("order_need_medication"),
      mobility: t("order_need_mobility"),
      hygiene: t("order_need_hygiene"),
      housekeeping: t("order_need_housekeeping"),
      appointments: t("order_need_appointments"),
    };
    return map[key];
  };

  const timelineLabel = (key: TimelineKey) => {
    const map: Record<TimelineKey, string> = {
      asap: t("order_timeline_asap"),
      "1_3_months": t("order_timeline_1_3_months"),
      exploring: t("order_timeline_exploring"),
    };
    return map[key];
  };

  const contactMethodLabel = (key: ContactMethodKey) => {
    const map: Record<ContactMethodKey, string> = {
      email: t("order_contact_method_email"),
      phone: t("order_contact_method_phone"),
      whatsapp: t("order_contact_method_whatsapp"),
    };
    return map[key];
  };

  const handleSubmit = async () => {
    if (!selectedPlan) {
      setError(t("order_fill_plan"));
      return;
    }
    if (!name.trim() || !email.trim()) {
      setError(t("order_fill_fields"));
      return;
    }
    if (!phone.trim()) {
      setError(t("order_fill_phone"));
      return;
    }
    if (!parentCountry) {
      setError(t("order_fill_country"));
      return;
    }
    if (!hasNeeds) {
      setError(t("order_fill_needs"));
      return;
    }

    const inquiryDetails: InquiryDetails = {
      parentName: parentName.trim() || undefined,
      parentAge: parentAge || undefined,
      parentCountry,
      parentCity: parentCity.trim() || undefined,
      livingSituation: livingSituation || undefined,
      careNeeds,
      careNeedsOther: careNeedsOther.trim() || undefined,
      timeline,
      preferredContact,
      message: message.trim() || undefined,
    };

    setSubmitting(true);
    setError(null);
    trackContactClicked("order_submit");

    try {
      await invokePublicEdgeFunction("show-interest", {
        plan: plan!.nameKey,
        planName: t(plan!.nameKey),
        price: plan!.price,
        customerEmail: email.trim(),
        customerName: name.trim(),
        customerPhone: phone.trim(),
        message: message.trim() || null,
        inquiryDetails,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-20 pb-12 px-4 flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="max-w-md w-full text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="font-display text-2xl sm:text-3xl text-foreground mb-3">
              {t("order_interest_success")}
            </h1>
            <p className="text-muted-foreground mb-6 text-base">
              {t("order_interest_success_desc")}
            </p>
            <Button variant="heroPrimary" size="lg" className="w-full sm:w-auto" asChild>
              <Link to="/">
                <ArrowLeft className="w-5 h-5 mr-2" />
                {t("order_back")}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const submitBlock = (
    <>
      {error && (
        <div
          ref={errorRef}
          role="alert"
          className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm rounded-lg p-3 mb-3"
        >
          {error}
        </div>
      )}
      <p className="text-muted-foreground text-sm mb-3 hidden sm:block">{t("order_interest_note")}</p>
      <Button
        variant="hero"
        size="lg"
        className="gap-2 w-full min-h-12 text-base touch-manipulation"
        onClick={handleSubmit}
        disabled={submitting}
      >
        {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Mail className="w-5 h-5" />}
        {submitting ? t("order_sending") : t("order_interest_cta")}
      </Button>
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 pt-20 pb-32 sm:pb-12 max-w-3xl">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 min-h-11 touch-manipulation"
        >
          <ArrowLeft className="w-4 h-4 shrink-0" />
          {t("order_back")}
        </Link>

        <span className="inline-block text-xs font-medium uppercase tracking-wide text-primary bg-primary/10 px-3 py-1.5 rounded-full mb-3">
          {t("order_market_badge")}
        </span>
        <h1 className="font-display text-2xl sm:text-3xl md:text-4xl text-foreground mb-2 leading-tight">
          {t("order_title")}
        </h1>
        <p className="text-muted-foreground mb-6 sm:mb-8 text-base leading-relaxed">{t("order_subtitle")}</p>

        {/* Mobile: compact progress */}
        <div className="sm:hidden mb-6">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-medium text-foreground">
              {steps[currentStep - 1]?.label}
            </span>
            <span className="text-muted-foreground">
              {currentStep}/{steps.length}
            </span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / steps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Desktop: step circles */}
        <div className="hidden sm:flex items-center justify-center mb-10">
          {steps.map((step, i) => (
            <div key={step.num} className="flex items-center">
              <div className="flex flex-col items-center">
                <motion.div
                  animate={{
                    backgroundColor: currentStep >= step.num ? "hsl(var(--primary))" : "hsl(var(--muted))",
                    scale: currentStep === step.num ? 1.08 : 1,
                  }}
                  transition={{ duration: 0.3 }}
                  className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold"
                >
                  {currentStep > step.num ? (
                    <Check className="w-5 h-5 text-primary-foreground" />
                  ) : (
                    <span className={currentStep >= step.num ? "text-primary-foreground" : "text-muted-foreground"}>
                      {step.num}
                    </span>
                  )}
                </motion.div>
                <span className={`text-xs mt-2 font-medium whitespace-nowrap ${
                  currentStep >= step.num ? "text-primary" : "text-muted-foreground"
                }`}>
                  {step.label}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div className="relative w-16 h-0.5 mx-2 mb-6 bg-muted overflow-hidden rounded-full">
                  <motion.div
                    className="absolute inset-y-0 left-0 bg-primary rounded-full"
                    animate={{ width: currentStep > step.num ? "100%" : "0%" }}
                    transition={{ duration: 0.4 }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Plan */}
        <div className={sectionClass}>
          <h3 className="font-display text-base sm:text-lg text-foreground mb-1 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-primary shrink-0" />
            <span>{t("order_select_plan")} *</span>
          </h3>
          <div className="space-y-3 mt-4">
            {cms.pricing.map((p) => (
              <button
                key={p.nameKey}
                type="button"
                onClick={() => {
                  setSelectedPlan(p.nameKey);
                  setError(null);
                  trackPlanSelected(p.nameKey, p.price);
                }}
                className={`w-full flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between p-4 rounded-xl border-2 transition-all text-left touch-manipulation active:scale-[0.99] ${
                  selectedPlan === p.nameKey
                    ? "border-primary bg-primary/5 shadow-md"
                    : "border-border hover:border-primary/50 bg-background"
                }`}
              >
                <div className="min-w-0 pr-2">
                  <span className="font-display text-lg text-foreground block">{t(p.nameKey)}</span>
                  <span className="text-muted-foreground text-sm block mt-0.5 leading-snug">{t(p.descKey)}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0 self-start sm:self-center">
                  <span className="font-display text-xl text-primary">{p.price}</span>
                  <span className="text-sm text-muted-foreground">{t("pricing_perMonth")}</span>
                  {selectedPlan === p.nameKey && <Check className="w-5 h-5 text-primary" />}
                </div>
              </button>
            ))}
          </div>
          <div className="mt-4">
            <label className={labelClass}>{t("order_timeline")}</label>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
              {TIMELINE_KEYS.map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTimeline(key)}
                  className={`${chipClass} w-full sm:w-auto ${
                    timeline === key
                      ? "border-primary bg-primary/10 text-primary font-medium"
                      : "border-border text-muted-foreground hover:border-primary/40"
                  }`}
                >
                  {timelineLabel(key)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Step 2: Parent */}
        <div className={sectionClass}>
          <h3 className="font-display text-base sm:text-lg text-foreground mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-primary shrink-0" />
            {t("order_parent_section")}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass} htmlFor="parent-name">{t("order_parent_name")}</label>
              <input id="parent-name" type="text" autoComplete="name" value={parentName} onChange={(e) => setParentName(e.target.value)} placeholder={t("order_parent_name_placeholder")} className={inputClass} />
            </div>
            <div>
              <label className={labelClass} htmlFor="parent-age">{t("order_parent_age")}</label>
              <select id="parent-age" value={parentAge} onChange={(e) => setParentAge(e.target.value as ParentAgeKey | "")} className={inputClass}>
                <option value="">—</option>
                {PARENT_AGE_KEYS.map((key) => (
                  <option key={key} value={key}>{ageLabel(key)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass} htmlFor="parent-country">{t("order_parent_country")} *</label>
              <select id="parent-country" value={parentCountry} onChange={(e) => setParentCountry(e.target.value as ServiceCountryKey | "")} className={inputClass} required>
                <option value="">—</option>
                {SERVICE_COUNTRY_KEYS.map((key) => (
                  <option key={key} value={key}>{countryLabel(key)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass} htmlFor="parent-city">{t("order_parent_city")}</label>
              <input id="parent-city" type="text" autoComplete="address-level2" value={parentCity} onChange={(e) => setParentCity(e.target.value)} placeholder={t("order_parent_city_placeholder")} className={inputClass} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>{t("order_living_situation")}</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mt-1">
                {LIVING_SITUATION_KEYS.map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setLivingSituation(key)}
                    className={`${chipClass} ${
                      livingSituation === key
                        ? "border-primary bg-primary/10 text-primary font-medium"
                        : "border-border text-muted-foreground hover:border-primary/40"
                    }`}
                  >
                    {livingLabel(key)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Step 3: Care needs */}
        <div className={sectionClass}>
          <h3 className="font-display text-base sm:text-lg text-foreground mb-1 flex items-center gap-2">
            <Heart className="w-5 h-5 text-primary shrink-0" />
            <span>{t("order_needs_section")} *</span>
          </h3>
          <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{t("order_needs_hint")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {CARE_NEED_KEYS.map((key) => (
              <label
                key={key}
                className={`flex items-center gap-3 min-h-12 p-3 rounded-lg border cursor-pointer transition touch-manipulation active:scale-[0.99] ${
                  careNeeds.includes(key) ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
                }`}
              >
                <input
                  type="checkbox"
                  checked={careNeeds.includes(key)}
                  onChange={() => toggleCareNeed(key)}
                  className="h-5 w-5 shrink-0 rounded border-border text-primary focus:ring-primary"
                />
                <span className="text-sm sm:text-base text-foreground leading-snug">{needLabel(key)}</span>
              </label>
            ))}
          </div>
          <div className="mt-4">
            <label className={labelClass} htmlFor="needs-other">{t("order_need_other")}</label>
            <textarea
              id="needs-other"
              rows={3}
              value={careNeedsOther}
              onChange={(e) => setCareNeedsOther(e.target.value)}
              placeholder={t("order_needs_other_placeholder")}
              className={`${inputClass} resize-y min-h-[5rem]`}
            />
          </div>
        </div>

        {/* Step 4: Contact */}
        <div className={sectionClass}>
          <h3 className="font-display text-base sm:text-lg text-foreground mb-4">{t("order_your_details")}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass} htmlFor="contact-name">{t("order_name")} *</label>
              <input id="contact-name" type="text" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("order_name_placeholder")} required className={inputClass} />
            </div>
            <div>
              <label className={labelClass} htmlFor="contact-email">{t("order_email")} *</label>
              <input id="contact-email" type="email" inputMode="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("order_email_placeholder")} required className={inputClass} />
            </div>
            <div>
              <label className={labelClass} htmlFor="contact-phone">{t("order_phone")} *</label>
              <input id="contact-phone" type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("order_phone_placeholder")} required className={inputClass} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>{t("order_preferred_contact")}</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1">
                {CONTACT_METHOD_KEYS.map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setPreferredContact(key)}
                    className={`${chipClass} ${
                      preferredContact === key
                        ? "border-primary bg-primary/10 text-primary font-medium"
                        : "border-border text-muted-foreground"
                    }`}
                  >
                    {contactMethodLabel(key)}
                  </button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass} htmlFor="contact-message">{t("order_message")}</label>
              <textarea id="contact-message" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t("order_message_placeholder")} className={`${inputClass} resize-y min-h-[5rem]`} />
            </div>
          </div>
        </div>

        {/* Desktop submit */}
        <div className="hidden sm:block bg-primary/5 border-2 border-primary/20 rounded-xl p-6 mb-8">
          {submitBlock}
        </div>

        <div className="border-t pt-6 sm:pt-8 pb-4">
          <h3 className="text-sm font-medium text-foreground mb-3">{t("order_contact_cta")}</h3>
          <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              onClick={() => trackContactClicked("order_contact_section")}
              className="inline-flex items-center gap-2 text-primary font-medium hover:underline break-all min-h-11 touch-manipulation"
            >
              <Mail className="w-4 h-4 shrink-0" />
              {CONTACT_EMAIL}
            </a>
            <a
              href={`tel:${(cms.contact.phone || "+381 11 123 4567").replace(/\s/g, "")}`}
              className="inline-flex items-center gap-2 text-primary font-medium hover:underline min-h-11 touch-manipulation"
            >
              <Phone className="w-4 h-4 shrink-0" />
              {cms.contact.phone || "+381 11 123 4567"}
            </a>
          </div>
        </div>
      </div>

      {/* Mobile: sticky submit bar */}
      <div className="sm:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background/95 backdrop-blur-md px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
        <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{t("order_interest_note")}</p>
        {submitBlock}
      </div>
    </div>
  );
}
